'use client'

// VideoContext that coordinates between components
// Video elements are managed by useVideoElement hook in components

import type VideoChapter from '@/types/VideoChapter'
import { getVideoPathForChapter } from '@/utils/videoUtils'
import { type ReactNode, createContext, useContext, useState } from 'react'
import { useUserPrefs } from './UserPrefsContext'

interface VideoState {
  currentVideo: string | null
  currentVideoRequestId: number
  isPlaying: boolean
  // Video tracks should not have any audio tracks,
  // so we disable volume/mute controls for now
  //   isMuted: boolean
  //   volume: number
  position: number
  duration: number
  videoReady: boolean
}

interface VideoContextType extends VideoState {
  play: () => void
  pause: () => void
  // Video tracks should not have any audio tracks,
  // so we disable volume/mute controls for now
  //   setVolume: (volume: number) => void
  //   setMuted: (muted: boolean) => void
  seek: (position: number) => void
  setCurrentVideo: (chapter: VideoChapter | null) => void
  setVideoReady: (ready: boolean) => void
  setDuration: (duration: number) => void
}

const initialState: VideoState = {
  currentVideo: null,
  currentVideoRequestId: 0,
  isPlaying: false,
  // Video tracks should not have any audio tracks,
  // so we disable volume/mute controls for now
  //   isMuted: true,
  //   volume: 0,
  position: 0,
  duration: 0,
  videoReady: false,
}

const VideoContext = createContext<VideoContextType | undefined>(undefined)

export function VideoProvider({ children }: { children: ReactNode }) {
  const { userPrefs } = useUserPrefs()
  const [state, setState] = useState<VideoState>(initialState)

  // Simplified setter - no complex change detection needed
  const setter = (newVal: Partial<VideoState>) => {
    setState((prev) => ({ ...prev, ...newVal }))
  }

  const play = () => {
    if (state.isPlaying) return
    console.log(['Play video'], 'video')
    setter({ isPlaying: true })
  }
  const pause = () => {
    if (!state.isPlaying) return
    console.log(['Pause video'], 'video')
    setter({ isPlaying: false })
  }
  // Video tracks should not have any audio tracks,
  // so we disable volume/mute controls for now
  //   const setVolume = (volume: number) => setter({ volume })
  //   const setMuted = (muted: boolean) => setter({ isMuted: muted })

  // Throttle position updates to avoid excessive re-renders
  // Only update if position changes by more than 0.05 seconds for smoother progress bar
  const seek = (position: number) => {
    setState((prev) => {
      if (Math.abs(prev.position - position) < 0.05) {
        return prev
      }
      return { ...prev, position }
    })
  }

  const setDuration = (duration: number) => {
    console.log(['Set duration', { duration }], 'video')
    setter({ duration })
  }
  const setVideoReady = (ready: boolean) => {
    console.log(['Video ready state changed', { ready }], 'video')
    setter({ videoReady: ready })
  }

  const setCurrentVideo = (chapter: VideoChapter | null) => {
    if (!chapter) {
      setState((prev) => ({
        ...prev,
        currentVideo: null,
        position: 0,
        duration: 0,
        videoReady: false,
        currentVideoRequestId: prev.currentVideoRequestId + 1,
      }))
      return
    }

    // Use the shared utility for video path selection
    const videoPath = getVideoPathForChapter(chapter, {
      isMobile: userPrefs.isMobile ?? false,
      useAV1: userPrefs.useAV1 ?? false,
    })

    console.log(['Setting current video', { videoPath, chapterId: chapter.id }], 'video')

    setState((prev) => ({
      ...prev,
      currentVideo: videoPath,
      position: 0,
      duration: 0,
      videoReady: false,
      currentVideoRequestId: prev.currentVideoRequestId + 1,
    }))
  }

  const value = {
    ...state,
    play,
    pause,
    // Video tracks should not have any audio tracks,
    // so we disable volume/mute controls for now
    // setVolume,
    // setMuted,
    seek,
    setCurrentVideo,
    setVideoReady,
    setDuration,
  }

  return <VideoContext.Provider value={value}>{children}</VideoContext.Provider>
}

export function useVideo() {
  const context = useContext(VideoContext)
  if (!context) throw new Error('useVideo must be used within a VideoProvider')
  return context
}
